import type { TemplateQaManifestEntry, TemplateQaProgressFile } from '../lib/templateQaClient';
import { comboDisplayLabel, progressCounts } from '../lib/templateQaClient';
import { Modal } from './Modal';

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${s}s`;
}

function etaLabel(elapsedMs: number, current: number, total: number): string | null {
  if (current <= 0 || total <= current) return null;
  const remaining = (elapsedMs / current) * (total - current);
  return formatElapsed(remaining);
}

function statusTitle(running: boolean, progress: TemplateQaProgressFile | null): string {
  const st = progress?.status;
  if (running && (st === 'starting' || !st)) return 'Warming up';
  if (running) return 'Capturing';
  if (st === 'failed') return 'Smoke failed';
  if (st === 'done_with_failures') return 'Done with failures';
  if (st === 'done') return 'Smoke complete';
  return 'Stack smoke';
}

export function SmokeProgressDialog({
  open,
  running,
  progress,
  elapsedMs,
  fatalError,
  downloading,
  onHide,
  onClose,
  onDownload,
}: {
  open: boolean;
  running: boolean;
  progress: TemplateQaProgressFile | null;
  elapsedMs: number;
  fatalError?: string | null;
  downloading: boolean;
  onHide: () => void;
  onClose: () => void;
  onDownload: () => void;
}) {
  const current = progress?.current ?? 0;
  const total = progress?.total ?? 0;
  const { passed, failed } = progressCounts(progress);
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  const nowLabel = comboDisplayLabel(progress?.currentCombo);
  const failures = (progress?.manifest ?? []).filter((m) => !m.ok);
  const eta = running ? etaLabel(elapsedMs, current, total) : null;
  const st = progress?.status;
  const barTone = st === 'failed' || (failed > 0 && !running) ? 'bg-nm-danger' : failed > 0 ? 'bg-nm-warn' : 'bg-nm-accent';
  const fatal = !running && (st === 'failed' || Boolean(fatalError));
  const fatalText = fatalError || progress?.message || 'The QA process stopped before finishing.';

  return (
    <Modal open={open} onClose={running ? onHide : onClose} title={statusTitle(running, progress)} wide>
      <div className="space-y-4">
        <div>
          <div className="flex items-baseline justify-between gap-3 font-mono text-[12px]">
            <span className="text-nm-ink">
              {total > 0 ? `${current} / ${total}` : 'Starting…'}
            </span>
            <span className="text-nm-muted">{total > 0 ? `${pct}%` : ''}</span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-nm-border/60">
            <div
              className={`h-full rounded-full transition-all duration-300 ${barTone}`}
              style={{ width: `${running ? pct : st === 'failed' ? pct : 100}%` }}
            />
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 font-mono text-[11px] sm:grid-cols-4">
          <div>
            <dt className="text-[9px] uppercase tracking-[0.14em] text-nm-muted">Passed</dt>
            <dd className="text-nm-success">{passed}</dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-[0.14em] text-nm-muted">Failed</dt>
            <dd className={failed > 0 ? 'text-nm-danger' : 'text-nm-muted'}>{failed}</dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-[0.14em] text-nm-muted">Elapsed</dt>
            <dd className="text-nm-ink">{formatElapsed(elapsedMs)}</dd>
          </div>
          <div>
            <dt className="text-[9px] uppercase tracking-[0.14em] text-nm-muted">ETA</dt>
            <dd className="text-nm-ink">{running ? eta ?? '—' : '—'}</dd>
          </div>
        </dl>

        {running && (
          <p className="font-mono text-[11px] text-nm-ink">
            {st === 'starting' || current === 0 ? (
              <span className="text-nm-muted">Starting Astro and Chromium…</span>
            ) : (
              <>
                <span className="text-[9px] uppercase tracking-[0.14em] text-nm-muted">Now capturing </span>
                {nowLabel || 'next combo'}
              </>
            )}
          </p>
        )}

        {fatal && (
          <div className="rounded-lg border border-nm-danger/30 bg-nm-danger-bg px-3 py-2 font-mono text-[11px] text-nm-danger" role="alert">
            {fatalText}
            {nowLabel ? <div className="mt-1 text-nm-muted">Last combo: {nowLabel}</div> : null}
          </div>
        )}

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <h3 className="font-mono text-[10px] uppercase tracking-[0.14em] text-nm-muted">Failures</h3>
            <span className="font-mono text-[10px] text-nm-muted">{failures.length}</span>
          </div>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-nm-border bg-nm-bg/40">
            {failures.length === 0 ? (
              <p className="px-3 py-4 font-mono text-[11px] text-nm-muted">
                {running ? 'No failures yet.' : 'No combo failures.'}
              </p>
            ) : (
              <ul className="divide-y divide-nm-border/70">
                {failures.map((m: TemplateQaManifestEntry) => (
                  <li key={m.slug} className="px-3 py-2 font-mono text-[11px]">
                    <div className="text-nm-ink">{comboDisplayLabel(m)}</div>
                    <div className="mt-0.5 text-nm-danger">{m.error ?? 'failed'}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {running ? (
            <button type="button" onClick={onHide} className="fp-btn fp-btn-ghost">
              Hide
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="fp-btn fp-btn-ghost">
                Close
              </button>
              <button
                type="button"
                disabled={downloading}
                onClick={onDownload}
                className="fp-btn fp-btn-primary"
              >
                {downloading ? 'Downloading…' : 'Download bundle'}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
